"use client";

import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, X, MapPin } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import { cn } from '@/lib/utils';
import MapPinSelector from '@/components/MapPinSelector';

interface StoreGetDirectionsSidebarProps {
  storeId: string;
  isOpen: boolean;
  onClose: () => void;
  addresses: Array<{
    id: string;
    label: string;
    location: {
      displayName: string;
      latitude: number;
      longitude: number;
    };
    is_primary?: boolean;
    floor?: string;
    apartment?: string;
    notes?: string;
  }>;
}

export const StoreGetDirectionsSidebar: React.FC<StoreGetDirectionsSidebarProps> = ({
  storeId,
  isOpen,
  onClose,
  addresses,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();

  const selectedAddress = addresses[selectedIndex];

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      recalculate();
      const timeoutId = setTimeout(recalculate, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, recalculate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Si el elemento ya no está en el DOM, probablemente fue desmontado por una acción
      // interna. Evitar cerrar el sidebar accidentalmente.
      if (target && !document.body.contains(target)) return;

      const isPortal = target.closest('[data-radix-portal]') || 
                      target.closest('[data-radix-popper-content-wrapper]') ||
                      target.closest('[role="dialog"]') ||
                      target.closest('[role="listbox"]') ||
                      target.closest('[role="menu"]') ||
                      target.closest('[role="option"]') ||
                      target.closest('[toast-close]') ||
                      target.closest('[role="status"]');

      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(target) && !isPortal) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) {
    return null;
  }

  if (!selectedAddress) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[9997]"
            onClick={handleClose}
          />

          {/* Sidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className="fixed right-0 w-full lg:w-[400px] bg-background z-[9998] flex flex-col"
            style={{
              top: isDesktop ? `${headerHeight}px` : 0,
              bottom: isDesktop ? undefined : 0,
              height: '100%',
            }}
          >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Direcciones
        </h2>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground" onClick={(e) => { e.stopPropagation(); handleClose(); }}>
                  <X className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Cerrar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Address Tabs */}
          {addresses.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {addresses.map((addr, idx) => (
                <button
                  key={addr.id}
                  onClick={() => setSelectedIndex(idx)}
                  className={cn(
                    'px-3 py-1 rounded text-sm transition-colors',
                    selectedIndex === idx
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  )}
                >
                  {addr.label || `Dirección ${idx + 1}`}
                  {addr.is_primary && <span className="ml-1">★</span>}
                </button>
              ))}
            </div>
          )}

          {/* Map */}
          <div className="flex flex-col gap-2">
            <div className="text-sm font-semibold">{selectedAddress.location.displayName}</div>
            <div className="w-full h-80 rounded overflow-hidden border">
              <MapPinSelector
                lat={selectedAddress.location.latitude}
                lon={selectedAddress.location.longitude}
                onChange={undefined}
                height="100%"
                draggable={false}
                clickable={false}
              />
            </div>

            {/* Extra info */}
            {(selectedAddress.floor || selectedAddress.apartment || selectedAddress.notes) && (
              <div className="text-sm space-y-1 bg-muted p-3 rounded">
                {selectedAddress.floor && (
                  <div><span className="text-muted-foreground">Piso:</span> {selectedAddress.floor}</div>
                )}
                {selectedAddress.apartment && (
                  <div><span className="text-muted-foreground">Depto:</span> {selectedAddress.apartment}</div>
                )}
                {selectedAddress.notes && (
                  <div><span className="text-muted-foreground">Indicaciones:</span> {selectedAddress.notes}</div>
                )}
              </div>
            )}
          </div>

          {/* Map Links */}
          <div className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground">Abrir en apps de mapas:</span>
            <button
              className="px-3 py-2 text-sm text-primary underline text-left hover:bg-muted p-2 rounded transition-colors"
              onClick={() => {
                const url = `https://www.google.com/maps/search/?api=1&query=${selectedAddress.location.latitude},${selectedAddress.location.longitude}`;
                window.open(url, "_blank");
              }}
            >
              <span className="inline-flex items-center gap-1">
                <ArrowRight className="h-3.5 w-3.5" />
                Google Maps
              </span>
            </button>
            <button
              className="px-3 py-2 text-sm text-primary underline text-left hover:bg-muted p-2 rounded transition-colors"
              onClick={() => {
                const url = `https://waze.com/ul?ll=${selectedAddress.location.latitude},${selectedAddress.location.longitude}&navigate=yes`;
                window.open(url, "_blank");
              }}
            >
              <span className="inline-flex items-center gap-1">
                <ArrowRight className="h-3.5 w-3.5" />
                Waze
              </span>
            </button>
            <button
              className="px-3 py-2 text-sm text-primary underline text-left hover:bg-muted p-2 rounded transition-colors"
              onClick={() => {
                const url = `https://maps.apple.com/?ll=${selectedAddress.location.latitude},${selectedAddress.location.longitude}`;
                window.open(url, "_blank");
              }}
            >
              <span className="inline-flex items-center gap-1">
                <ArrowRight className="h-3.5 w-3.5" />
                Apple Maps
              </span>
            </button>
          </div>

          <Button variant="outline" onClick={(e) => { e.stopPropagation(); handleClose(); }} className="w-full">
            Cerrar
          </Button>
        </div>
      </ScrollArea>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
